/**
 * Schorschi, the shared assistant: /api/v2/schorschi (src/schorschi/routes.ts).
 * Live updates arrive as the `schorschi` event on GET /api/v2/events
 * (api/events.ts → onSchorschiEvent).
 */

import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/vue-query";
import { computed, toValue, type MaybeRefOrGetter } from "vue";
import type { Mood, Reaction } from "@/components/schorschi/moods";
import { readSse } from "@/lib/sse";
import { api, http } from "./client";

const base = "/api/v2/schorschi";
const enc = encodeURIComponent;

export type Severity = "ok" | "info" | "notice" | "warning" | "critical";
export type JournalKind = "observation" | "note" | "investigation" | "digest" | "verification";
/** chat: one person; standup: listens, answers only when addressed, minutes afterwards; drive: voice-only, hands-free. */
export type VoiceMode = "chat" | "standup" | "drive";

export interface SchorschiBudget {
  dailyUsd: number;
  monthlyUsd: number;
  spentTodayUsd: number;
  spentMonthUsd: number;
  perPersonDailyUsd: number;
  mineTodayUsd: number;
  backgroundDailyUsd: number;
  backgroundTodayUsd: number;
  exhausted: boolean;
  resetsAt: string;
}

export interface SchorschiStatus {
  enabled: boolean;
  chat: boolean;
  /** live: your normal and drive calls run on GPT-Live (the route schorschi-live is on and open to you). */
  voice: { available: boolean; voice: string; maxMinutes: number; modes?: Record<VoiceMode, { maxMinutes: number }>; live?: boolean };
  mood: { mood: Mood; reason: string | null; at: string };
  budget: SchorschiBudget;
  canTalk: boolean;
  canAdmin: boolean;
}

export interface JournalEntry {
  id: string;
  kind: JournalKind;
  title: string;
  bodyMd: string;
  severity: Severity;
  mood: Mood | null;
  target: string | null;
  probeKey: string | null;
  private: boolean;
  jobId: string | null;
  costUsd: number;
  createdAt: string;
  checkedAt: string;
}

export interface Proposal {
  id: string;
  tool: string;
  argumentsPreview: Record<string, unknown>;
  reason: string;
  planSummary: string | null;
  target: string | null;
  status: "open" | "applied" | "dismissed";
  private: boolean;
  journalId: string | null;
  conversationId: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  createdAt: string;
}

export interface Visual {
  kind: "markdown" | "image" | "link" | "chart" | "table";
  title: string;
  markdown?: string;
  url?: string;
  upload?: string;
  chart?: { type: "bar" | "line"; labels: string[]; series: { name: string; values: number[] }[]; unit?: string };
  table?: { columns: string[]; rows: (string | number | null)[][] };
}

/** One tool call of an answer: which tool (through the gateway or not), the target, how long, what came back in one line. */
export interface ChatStep {
  name: string;
  via?: string;
  target?: string;
  ok: boolean;
  ms?: number;
  summary?: string;
}

/** A knowledge page or document an answer relied on (knowledge://entity/<type>/<slug> or knowledge://doc/<id>). */
export interface ChatSource {
  uri: string;
  title: string;
}

/** A file attached to a question (an upload). */
export interface ChatAttachment {
  id: string;
  name: string;
  mimeType: string;
  size: number;
}

export interface ChatMessage {
  id: number;
  role: "user" | "assistant" | "tool";
  content: string;
  createdAt: string;
  mood?: Mood;
  reaction?: Reaction;
  visuals?: Visual[];
  proposals?: string[];
  tools?: { name: string; ok: boolean }[];
  steps?: ChatStep[];
  sources?: ChatSource[];
  followups?: string[];
  attachments?: ChatAttachment[];
  /** The person stopped the answer; it holds what was written by then. */
  stopped?: boolean;
  channel?: string;
  costUsd: number;
  /** Minutes of the conversation (POST …/summary or a standup's end); its proposals are the action items. */
  summary?: boolean;
}

export interface Conversation {
  id: string;
  channel: string;
  title: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SendResult {
  conversationId: string;
  userMessage: ChatMessage;
  reply: ChatMessage;
  proposals: Proposal[];
  costUsd: number;
  stopReason: string;
  /** Messages this answer replaced (asked again or edited); none when it failed, ran out of budget or was stopped. */
  superseded?: number[];
}

/** A question to start with (GET /schorschi/starters); `send: false` goes into the composer to finish. */
export interface Starter {
  text: string;
  kind: "picture" | "proposal" | "investigation" | "target" | "tool" | "general";
  send: boolean;
}

/** What goes to Schorschi: a question (with files), or the last one again / edited. */
export interface SendBody {
  conversationId?: string;
  text?: string;
  attachments?: string[];
  regenerate?: boolean;
  edit?: boolean;
}

/** The live answer (POST /schorschi/messages/stream), event by event (src/schorschi/routes.ts). */
export type ChatStreamEvent =
  | { type: "start"; conversationId: string; conversation: Conversation; userMessage: ChatMessage; superseded: number[] }
  | { type: "turn"; turn: number }
  | { type: "delta"; turn: number; text: string }
  | { type: "reset"; turn: number }
  | { type: "step"; step: ChatStep & { id: number; phase: "start" | "end"; proposalId?: string } }
  | { type: "visual"; visual: Visual }
  | { type: "expression"; mood?: Mood; reaction?: Reaction }
  | { type: "proposal"; id: string; tool: string; summary: string }
  | { type: "followups"; questions: string[] }
  | ({ type: "done" } & SendResult)
  | { type: "error"; status: number; title: string; detail?: string; code?: string; resetsAt?: string };

export interface Memory {
  id: string;
  about: string | null;
  note: string;
  mine: boolean;
  source: string;
  createdAt: string;
}

export interface AdminStatus {
  probes: { key: string; title: string; tool: string; everyMinutes: number; lastRunAt: string | null; lastStatus: string | null; lastError: string | null }[];
  signals: { id: number; kind: string; summary: string; target: string | null; decision: string | null; reason: string | null; journalId: string | null; createdAt: string }[];
}

/** Minutes of a conversation (POST …/conversations/:id/summary): the action items are open todo proposals. */
export interface Minutes {
  conversationId: string;
  message: ChatMessage;
  title: string;
  decisions: string[];
  blockers: { text: string; who: string | null }[];
  proposals: Proposal[];
  costUsd: number;
}

export interface MinutesApplied {
  items: { proposal: Proposal; isError: boolean; message: string }[];
}

export interface VoiceStarted {
  sessionId: string;
  conversationId: string;
  sdp: string;
  model: string;
  voice: string;
  mode: VoiceMode;
  maxSeconds: number;
  idleSeconds: number;
  /** Earlier calls of yours that this one replaced (a dropped line, another device). */
  replaced: number;
  /** GPT-Live (live) or OpenAI Realtime (realtime): decides which events the browser reads from the data channel. */
  protocol: "realtime" | "live";
}

export interface ApplyResult {
  proposal: Proposal;
  isError: boolean;
  content: { type: string; text?: string }[];
  structuredContent?: Record<string, unknown>;
}

// --- Queries ---------------------------------------------------------------------

export const schorschiKeys = {
  all: ["schorschi"] as const,
  status: ["schorschi", "status"] as const,
  picture: ["schorschi", "picture"] as const,
  feed: (filter: unknown) => ["schorschi", "feed", filter] as const,
  conversations: ["schorschi", "conversations"] as const,
  conversationSearch: (q: string) => ["schorschi", "conversations", q] as const,
  starters: ["schorschi", "starters"] as const,
  conversation: (id: string | null) => ["schorschi", "conversation", id] as const,
  proposals: (status?: string) => ["schorschi", "proposals", status ?? "all"] as const,
  /** Every proposals list (open, applied, all): to refresh them together. */
  proposalLists: ["schorschi", "proposals"] as const,
  memories: ["schorschi", "memories"] as const,
  admin: ["schorschi", "admin"] as const,
};

export function useSchorschiStatus() {
  return useQuery({ queryKey: schorschiKeys.status, queryFn: () => http.get<SchorschiStatus>(base), refetchInterval: 60_000 });
}

export function usePicture() {
  return useQuery({ queryKey: schorschiKeys.picture, queryFn: () => http.get<{ items: JournalEntry[] }>(`${base}/picture`).then((r) => r.items) });
}

export function useFeed(filter: MaybeRefOrGetter<{ kind?: JournalKind; q?: string }>) {
  return useInfiniteQuery({
    queryKey: computed(() => schorschiKeys.feed(toValue(filter))),
    queryFn: ({ pageParam }) =>
      http.get<{ items: JournalEntry[]; nextBefore: string | null }>(`${base}/feed`, { limit: 20, ...toValue(filter), ...(pageParam ? { before: pageParam } : {}) }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextBefore ?? undefined,
  });
}

export function useConversations() {
  return useQuery({ queryKey: schorschiKeys.conversations, queryFn: () => http.get<{ items: Conversation[] }>(`${base}/conversations`).then((r) => r.items) });
}

/** Conversations, latest first, searched by title (the messages are encrypted on the server), page by page. */
export function useConversationSearch(q: MaybeRefOrGetter<string>) {
  return useInfiniteQuery({
    queryKey: computed(() => schorschiKeys.conversationSearch(toValue(q).trim())),
    queryFn: ({ pageParam }) => http.get<{ items: Conversation[]; nextBefore: string | null }>(`${base}/conversations`, { limit: 30, q: toValue(q).trim(), ...(pageParam ? { before: pageParam } : {}) }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextBefore ?? undefined,
  });
}

/** Questions to start with, from what the person may see right now (no model call). */
export function useStarters(enabled: MaybeRefOrGetter<boolean> = true) {
  return useQuery({ queryKey: schorschiKeys.starters, queryFn: () => http.get<{ items: Starter[] }>(`${base}/starters`).then((r) => r.items), enabled: computed(() => toValue(enabled)), staleTime: 60_000 });
}

export function useConversation(id: MaybeRefOrGetter<string | null>) {
  return useQuery({
    queryKey: computed(() => schorschiKeys.conversation(toValue(id))),
    // minutesPending: the minutes are being written, or a standup call in it is still ending (no need to ask for them).
    queryFn: () => http.get<{ conversation: Conversation; messages: ChatMessage[]; proposals: Proposal[]; minutesPending: boolean }>(`${base}/conversations/${enc(toValue(id)!)}`),
    enabled: computed(() => Boolean(toValue(id))),
  });
}

export function useProposals(status: MaybeRefOrGetter<"open" | "applied" | "dismissed" | undefined>) {
  return useQuery({
    queryKey: computed(() => schorschiKeys.proposals(toValue(status))),
    queryFn: () => http.get<{ items: Proposal[] }>(`${base}/proposals`, toValue(status) ? { status: toValue(status)! } : {}).then((r) => r.items),
  });
}

export function useMemories() {
  return useQuery({ queryKey: schorschiKeys.memories, queryFn: () => http.get<{ items: Memory[] }>(`${base}/memories`).then((r) => r.items) });
}

export function useAdminStatus(enabled: MaybeRefOrGetter<boolean>) {
  return useQuery({ queryKey: schorschiKeys.admin, queryFn: () => http.get<AdminStatus>(`${base}/admin/status`), enabled: computed(() => toValue(enabled)), refetchInterval: 30_000 });
}

/** Refresh everything Schorschi shows (after a change of ours or a live event). */
export function useSchorschiRefresh() {
  const client = useQueryClient();
  return (key: readonly unknown[] = schorschiKeys.all) => client.invalidateQueries({ queryKey: key });
}

// --- Mutations -------------------------------------------------------------------

export const sendMessage = (body: SendBody) => http.post<SendResult>(`${base}/messages`, body);
export const deleteConversation = (id: string) => http.del<{ deleted: boolean }>(`${base}/conversations/${enc(id)}`);
export const renameConversation = (id: string, title: string) => http.patch<{ conversation: Conversation }>(`${base}/conversations/${enc(id)}`, { title });

/**
 * Ask and follow the answer live: `onEvent` gets each event in order until `done` or `error`.
 * Throws an `ApiProblem` when the turn is refused before it starts (asleep, no model, budget, …),
 * and the abort error when `signal` stops it (the server keeps what was written, marked stopped).
 */
export async function streamMessage(body: SendBody, onEvent: (e: ChatStreamEvent) => void, signal?: AbortSignal): Promise<void> {
  const response = await http.stream(`${base}/messages/stream`, body, signal);
  for await (const { event, data } of readSse(response)) {
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(data) as Record<string, unknown>;
    } catch {
      continue;
    }
    onEvent({ ...parsed, type: event } as ChatStreamEvent);
  }
}
export const applyProposal = (id: string) => http.post<ApplyResult>(`${base}/proposals/${enc(id)}/apply`);
export const dismissProposal = (id: string) => http.post<{ proposal: Proposal }>(`${base}/proposals/${enc(id)}/dismiss`);
export const forgetMemory = (id: string) => http.del<{ deleted: boolean }>(`${base}/memories/${enc(id)}`);
export const requestResearch = (body: { goal: string; scope?: string }) => http.post<{ jobId: string }>(`${base}/research`, body);
export const runNow = (what: "probes" | "garden" | "verify") => http.post<{ jobId: string | null }>(`${base}/admin/run`, { what });
/** `protocol` asks for Realtime or GPT-Live (the server uses GPT-Live only where it is open); `headset`: the microphone is one (near-field noise reduction). */
export const startVoiceCall = (body: { sdp: string; conversationId?: string; mode?: VoiceMode; protocol?: "realtime" | "live"; headset?: boolean }) => http.post<VoiceStarted>(`${base}/voice`, body);
export const endVoiceCall = (sessionId: string) => http.post<{ ended: boolean }>(`${base}/voice/${enc(sessionId)}/end`);
/** Hang up while the page goes away (tab closed, the phone dropped it): a keepalive request outlives the page. */
export function endVoiceCallOnExit(sessionId: string): void {
  void api.POST("/api/v2/schorschi/voice/{id}/end", { params: { path: { id: sessionId } }, keepalive: true }).catch(() => undefined);
}
/** Minutes of a conversation (a standup): decisions, blockers, action items as todo proposals. One model call (10–60 s). */
export const summarizeConversation = (id: string) => http.post<Minutes>(`${base}/conversations/${enc(id)}/summary`);
/** Create the minutes' action items as todos (as you, through todo_change): all open ones, or `ids`. */
export const applyMinutes = (id: string, ids?: string[]) => http.post<MinutesApplied>(`${base}/conversations/${enc(id)}/summary/apply`, ids ? { ids } : {});

// --- Live events -------------------------------------------------------------------

/** The `schorschi` SSE event (src/schorschi/relay.ts). */
export type SchorschiEvent =
  | { kind: "mood"; mood: Mood; reason: string | null; at: string }
  | { kind: "gesture"; reaction: Reaction; mood?: Mood }
  | { kind: "journal"; entry: Pick<JournalEntry, "id" | "kind" | "title" | "severity" | "mood" | "target" | "private" | "createdAt"> }
  | {
      kind: "activity";
      conversationId?: string;
      activity?: "thinking" | "working" | "idle";
      tool?: string;
      mood?: Mood;
      reaction?: Reaction;
      visual?: Visual;
      researchDone?: { journalId: string; title: string; severity: Severity };
    }
  | { kind: "voice"; sessionId: string; event: VoiceEvent };

export type VoiceEvent =
  | { type: "transcript"; role: "user" | "assistant"; text: string }
  | { type: "expression"; mood?: Mood; reaction?: Reaction }
  | { type: "visual"; visual: Visual }
  | { type: "tool"; name: string; phase: "start" | "end"; ok?: boolean }
  | { type: "proposal"; id: string; tool: string; summary: string }
  | { type: "listening" }
  | { type: "responding" }
  /** Standup: someone said "Schorschi, …" — the answer starts now, after the current one, or not (the call is over). */
  | { type: "addressed"; state: "started" | "queued" | "ended" }
  /** Server-side timing of a turn (end of speech → first words, or → the answer after tools), in milliseconds. */
  | { type: "latency"; kind: "first" | "answer"; ms: number }
  | { type: "error"; message: string }
  | { type: "ended"; reason: string; seconds: number; costUsd: number };

const listeners = new Set<(e: SchorschiEvent) => void>();

export function onSchorschiEvent(listener: (e: SchorschiEvent) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Called by the live event stream (api/events.ts). */
export function dispatchSchorschiEvent(e: SchorschiEvent): void {
  for (const l of listeners) {
    try {
      l(e);
    } catch {
      // one listener's bug must not stop the others
    }
  }
}
